import { useState, useEffect, useMemo } from 'react'
import { motion } from 'framer-motion'
import { HiBuildingOffice2, HiPlus, HiTrash, HiCheckCircle } from 'react-icons/hi2'
import { FaUsers } from 'react-icons/fa'
import { Button } from '../../../components'
import { SectorSelect } from '../../../components/Select/SectorSelect'
import { BaseModal } from '../../../components/Modal/BaseModal'
import type { User, Setor } from '../../../models'
import { getSetorType, getSetorTypeLabel } from '../../../models'
import { useUsers } from '../hooks/useUserQueries'

interface ManageUserSectorsModalProps {
  isOpen: boolean
  user: User | null
  allSetores: Setor[]
  onClose: () => void
  onVincular: (setorId: number, usuariosIds: number[]) => Promise<boolean>
  onDesvincular: (usuarioId: number, setorId: number) => Promise<boolean>
}

export const ManageUserSectorsModal = ({
  isOpen,
  user: userProp,
  allSetores,
  onClose,
  onVincular,
  onDesvincular,
}: ManageUserSectorsModalProps) => {
  const [selectedSetorId, setSelectedSetorId] = useState<string>('')
  const [isAdding, setIsAdding] = useState(false)
  const [removingSetorId, setRemovingSetorId] = useState<number | null>(null)

  // Buscar dados atualizados do usuário do TanStack Query
  const { data: allUsers = [] } = useUsers()
  
  // Usar o usuário atualizado do cache se disponível, senão usar o prop
  const user = useMemo(() => {
    if (!userProp) return null
    const updatedUser = allUsers.find(u => u.id === userProp.id)
    return updatedUser || userProp
  }, [userProp, allUsers])

  useEffect(() => {
    if (isOpen) {
      setSelectedSetorId('')
      setIsAdding(false)
      setRemovingSetorId(null)
    }
  }, [isOpen])

  if (!user) return null

  const userSetorIds = user.setores?.map(s => s.id) || []
  const availableSetores = allSetores.filter(s => !userSetorIds.includes(s.id))

  const handleAddSetor = async () => {
    if (!selectedSetorId) return

    setIsAdding(true)
    await onVincular(parseInt(selectedSetorId), [user.id])
    setIsAdding(false)
    setSelectedSetorId('')
  }

  const handleRemoveSetor = async (setorId: number) => {
    setRemovingSetorId(setorId)
    await onDesvincular(user.id, setorId)
    setRemovingSetorId(null)
  }

  const setorOptions = availableSetores.map(setor => ({
    value: setor.id.toString(),
    label: setor.nome_setor,
    nome_setor: setor.nome_setor,
    descricao: getSetorTypeLabel(setor),
  }))

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title='Gerenciar Setores'
      icon={<HiBuildingOffice2 className='w-6 h-6' />}
      description={user.name}
      size='xl'
      useStandardLayout={true}
      resizable={true}
      stickyFooter={true}
      footerContent={
        <div className='flex items-center justify-end p-4'>
          <Button variant='primary' onClick={onClose}>
            Concluído
          </Button>
        </div>
      }
    >
      <div className='p-6 space-y-6'>
        {/* Add Setor Section */}
        <div className=''>
          <div className='flex items-center gap-2 mb-4'>
            <div className='p-2 bg-green-100 dark:bg-green-900/40 rounded-lg'>
              <HiPlus className='w-5 h-5 text-green-600 dark:text-green-400' />
            </div>
            <h4 className='text-base font-semibold text-gray-900 dark:text-gray-100'>
              Adicionar Novo Setor
            </h4>
          </div>
          <div className='flex flex-col gap-3'>
            <SectorSelect
              options={setorOptions}
              value={selectedSetorId}
              onChange={value => setSelectedSetorId(value.toString())}
              placeholder='Selecione um setor ou equipe...'
              disabled={isAdding || availableSetores.length === 0}
              searchable={true}
            />
            <Button
              onClick={handleAddSetor}
              disabled={!selectedSetorId || isAdding}
              variant='primary'
              className='w-full bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600 flex items-center justify-center gap-2'
            >
              <HiPlus className='w-4 h-4' />
              {isAdding ? 'Adicionando...' : 'Vincular Setor'}
            </Button>
          </div>
          {availableSetores.length === 0 && (
            <div className='mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg'>
              <p className='text-sm text-blue-800 dark:text-blue-200'>
                ✓ Todos os setores disponíveis já estão vinculados a este usuário.
              </p>
            </div>
          )}
        </div>

        {/* Current Setores */}
        <div>
          <div className='flex items-center justify-between mb-4'>
            <div className='flex items-center gap-2'>
              <div className='p-2 bg-blue-100 dark:bg-blue-900/40 rounded-lg'>
                <HiBuildingOffice2 className='w-5 h-5 text-blue-600 dark:text-blue-400' />
              </div>
              <h4 className='text-base font-semibold text-gray-900 dark:text-gray-100'>
                Setores Vinculados
              </h4>
            </div>
            <span className='px-3 py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-full text-sm font-semibold'>
              {user.setores?.length || 0}
            </span>
          </div>

          {user.setores && user.setores.length > 0 ? (
            <div className='space-y-3'>
              {user.setores.map(setor => {
                const entityType = getSetorType(setor)
                const Icon = entityType === 'equipe' ? FaUsers : HiBuildingOffice2
                const funcao = setor.pivot?.funcao || 'USUARIO'
                const funcaoLabel = funcao === 'GESTOR' ? 'SUPERIOR' : 'MEMBRO'

                return (
                  <motion.div
                    key={setor.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    className='group relative flex items-center justify-between p-4 bg-white dark:bg-neutral-800 rounded-lg border-2 border-gray-200 dark:border-neutral-700 hover:border-blue-300 dark:hover:border-blue-700 transition-all shadow-sm hover:shadow-md'
                  >
                    <div className='flex items-center gap-4 flex-1 min-w-0'>
                      {/* Icon */}
                      <div
                        className={`flex-shrink-0 p-3 rounded-lg ${
                          entityType === 'equipe'
                            ? 'bg-purple-100 dark:bg-purple-900/30'
                            : 'bg-blue-100 dark:bg-blue-900/30'
                        }`}
                      >
                        <Icon
                          className={`w-6 h-6 ${
                            entityType === 'equipe'
                              ? 'text-purple-600 dark:text-purple-400'
                              : 'text-blue-600 dark:text-blue-400'
                          }`}
                        />
                      </div>

                      {/* Info */}
                      <div className='flex-1 min-w-0'>
                        <div className='flex items-center gap-2 mb-1'>
                          <h5 className='font-semibold text-gray-900 dark:text-gray-100 truncate'>
                            {setor.nome_setor}
                          </h5>
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                              entityType === 'equipe'
                                ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300'
                                : 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                            }`}
                          >
                            {entityType === 'equipe' ? 'Equipe' : 'Setor'}
                          </span>
                        </div>
                        <div className='flex items-center gap-2'>
                          <p className='text-xs text-gray-500 dark:text-gray-400'>Função:</p>
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                              funcao === 'GESTOR'
                                ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300'
                                : 'bg-gray-100 dark:bg-neutral-700 text-gray-600 dark:text-gray-300'
                            }`}
                          >
                            {funcaoLabel}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Remove Button */}
                    <Button
                      variant='ghost'
                      size='sm'
                      onClick={() => handleRemoveSetor(setor.id)}
                      disabled={removingSetorId === setor.id}
                      className='flex-shrink-0 ml-3 text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/30'
                    >
                      {removingSetorId === setor.id ? (
                        <div className='flex items-center gap-2'>
                          <div className='w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin' />
                          <span className='text-xs'>Removendo...</span>
                        </div>
                      ) : (
                        <div className='flex items-center gap-2'>
                          <HiTrash className='w-4 h-4' />
                          <span className='text-xs font-medium'>Remover</span>
                        </div>
                      )}
                    </Button>
                  </motion.div>
                )
              })}
            </div>
          ) : (
            <div className='text-center py-12 bg-gray-50 dark:bg-neutral-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-neutral-700'>
              <HiBuildingOffice2 className='w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4' />
              <h5 className='text-base font-semibold text-gray-900 dark:text-gray-100 mb-2'>
                Nenhum setor vinculado
              </h5>
              <p className='text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto'>
                Este usuário ainda não está vinculado a nenhum setor ou equipe. Use o formulário
                acima para adicionar.
              </p>
            </div>
          )}
        </div>

        {/* Info */}
        <div className='bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-2 border-blue-200 dark:border-blue-800 rounded-lg p-4'>
          <div className='flex gap-3'>
            <div className='flex-shrink-0'>
              <div className='p-2 bg-blue-100 dark:bg-blue-900/40 rounded-lg'>
                <HiCheckCircle className='w-5 h-5 text-blue-600 dark:text-blue-400' />
              </div>
            </div>
            <div>
              <h5 className='font-semibold text-blue-900 dark:text-blue-100 mb-1'>
                Sobre Vínculos de Setores
              </h5>
              <p className='text-sm text-blue-800 dark:text-blue-200 leading-relaxed'>
                Ao vincular um usuário a um setor ou equipe, ele terá acesso para criar, visualizar
                e gerenciar demandas relacionadas. A função{' '}
                <span className='font-semibold'>SUPERIOR</span> concede permissões elevadas para
                autorizar e gerenciar processos.
              </p>
            </div>
          </div>
        </div>
      </div>
    </BaseModal>
  )
}
